/**
 * The documents the company sends over and over, and the one thing about them
 * that is a rule rather than a preference: when they stop being true.
 *
 * A residential roofer's licence and a certificate of insurance both carry a
 * printed expiry. Attaching an expired licence to a bid is worse than attaching
 * nothing — it looks deliberate to a general contractor, and it is the kind of
 * thing an adjuster keeps. So the library says so before it happens.
 *
 * It warns; it does not block. The day a licence lapses is not the day the
 * office should lose the ability to send a warranty brochure, and the person
 * who can fix a lapsed licence is not the person holding the phone at a kitchen
 * table.
 */

/** Just enough of a document row to judge it. */
export interface CompanyDocumentLike {
  name: string;
  /** ISO date, `YYYY-MM-DD`. Null for anything that does not expire. */
  expires_at: string | null;
}

export interface ExpiryNotice {
  expired: boolean;
  /** Days until it expires. Negative once it has. */
  days: number;
  label: string;
}

/**
 * How near the expiry has to be before it is worth saying anything.
 *
 * Sixty days is roughly the notice period that lets somebody renew a state
 * licence without paying for the hurry.
 */
export const EXPIRY_WARNING_DAYS = 60;

const DAY = 24 * 60 * 60 * 1000;

/**
 * Days from `today` to `date`, both ISO dates, counted in whole days.
 *
 * Parsed as UTC midnight on purpose. A local-time parse makes the answer depend
 * on the reader's timezone, and a licence does not expire an hour earlier in
 * Denver.
 */
export function daysBetween(today: string, date: string): number | null {
  const from = Date.parse(`${today}T00:00:00Z`);
  const to = Date.parse(`${date}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.round((to - from) / DAY);
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** "31 Dec 2026". Written out, because 12/01 means two different days. */
export function formatExpiryDate(date: string): string {
  const parts = date.split("-");
  if (parts.length !== 3) return date;
  const [year, month, day] = parts;
  const index = Number(month) - 1;
  if (!MONTHS[index]) return date;
  return `${Number(day)} ${MONTHS[index]} ${year}`;
}

/**
 * What to say about a document's expiry, or nothing at all.
 *
 * Null means silence: either it does not expire, or the date is far enough away
 * that mentioning it is noise. A warning that appears on every document every
 * day is a warning nobody reads on the day it matters.
 */
export function companyDocumentExpiry(
  document: CompanyDocumentLike,
  today: string,
): ExpiryNotice | null {
  if (!document.expires_at) return null;

  const days = daysBetween(today, document.expires_at);
  if (days == null) return null;

  if (days < 0) {
    return {
      expired: true,
      days,
      label: `Expired ${formatExpiryDate(document.expires_at)}`,
    };
  }
  if (days > EXPIRY_WARNING_DAYS) return null;
  if (days === 0) {
    return { expired: false, days, label: "Expires today" };
  }
  return {
    expired: false,
    days,
    label: `Expires in ${days} day${days === 1 ? "" : "s"}`,
  };
}

/**
 * The ones a person should be told about before an estimate goes out.
 *
 * Said once, over the set that is actually attached, rather than per row: a rep
 * about to present is deciding whether to send at all, and the useful sentence
 * names the documents.
 */
export function expiredAttachments(
  documents: readonly CompanyDocumentLike[],
  today: string,
): CompanyDocumentLike[] {
  return documents.filter(
    (document) => companyDocumentExpiry(document, today)?.expired === true,
  );
}
